<template>
  <div class="v2-brand-preview">
    <!-- Logo -->
    <div class="v2-brand-preview__logo-wrap">
      <img
        v-if="brandDetails.logo && !logoError"
        :src="brandLogo"
        alt="Brand Logo"
        class="v2-brand-preview__logo"
        @error="logoError = true"
      >
      <div
        v-else
        class="v2-brand-preview__logo-fallback"
        :style="{ backgroundColor: avatarColor }"
      >
        {{ brandInitial }}
      </div>
    </div>

    <!-- Name -->
    <div class="v2-brand-preview__name">
      {{ brandDetails.entityName }}
    </div>

    <!-- Header Info: location, website, instagram -->
    <div class="v2-brand-preview__info">
      <div
        v-if="brandDetails.country"
        class="v2-brand-preview__info-row"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="v2-brand-preview__info-icon"
        >
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
          <circle
            cx="12"
            cy="10"
            r="3"
          />
        </svg>
        <span>{{ brandDetails.country }}</span>
      </div>
      <a
        v-if="brandDetails.websiteLink"
        class="v2-brand-preview__info-row v2-brand-preview__info-row--link"
        :href="getLinkWithhttp(brandDetails.websiteLink)"
        target="_blank"
        rel="noopener noreferrer"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="v2-brand-preview__info-icon"
        >
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
          <polyline points="15 3 21 3 21 9" />
          <line
            x1="10"
            y1="14"
            x2="21"
            y2="3"
          />
        </svg>
        <span>{{ brandDetails.websiteLink }}</span>
      </a>
      <a
        v-if="brandDetails.instagramLink"
        class="v2-brand-preview__info-row v2-brand-preview__info-row--link"
        :href="getLinkWithhttp(brandDetails.instagramLink)"
        target="_blank"
        rel="noopener noreferrer"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="v2-brand-preview__info-icon"
        >
          <rect
            x="2"
            y="2"
            width="20"
            height="20"
            rx="5"
            ry="5"
          />
          <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
          <line
            x1="17.5"
            y1="6.5"
            x2="17.51"
            y2="6.5"
          />
        </svg>
        <span>{{ instagramHandle }}</span>
      </a>
    </div>

    <!-- Departments -->
    <div
      v-if="brandDetails.departments && brandDetails.departments.length"
      class="v2-brand-preview__departments"
    >
      <v2-badge
        v-for="department in brandDetails.departments.slice(0, 3)"
        :key="department"
        variant="outline"
      >
        {{ department }}
      </v2-badge>
    </div>

    <!-- Retailer Actions -->
    <div
      v-if="isRetailer"
      class="v2-brand-preview__actions"
    >
      <v2-button
        variant="secondary"
        block
        @click="showMeetingModal = true"
      >
        Book a Meeting
      </v2-button>
      <v2-button
        v-if="monthsWithCollection.length"
        :variant="isSubscribed ? 'outline' : 'primary'"
        block
        @click="onHandleSubscription"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          <template v-if="isSubscribed">
            <line
              x1="1"
              y1="1"
              x2="23"
              y2="23"
            />
          </template>
        </svg>
        {{ isSubscribed ? 'Unfollow Brand' : 'Follow Brand' }}
      </v2-button>
    </div>

    <!-- Prices (hidden) -->
    <!-- <div class="v2-brand-preview__prices">
      <div
        v-if="brandDetails.avgWholesalePrice"
        class="v2-brand-preview__price-row"
      >
        <span>Wholesale prices</span>
        <span>{{ brandDetails.avgWholesalePrice }}</span>
      </div>
      <div
        v-if="brandDetails.avgRetailPrice"
        class="v2-brand-preview__price-row"
      >
        <span>Retail prices</span>
        <span>{{ brandDetails.avgRetailPrice }}</span>
      </div>
      <div
        v-if="brandDetails.minimumOrderValue"
        class="v2-brand-preview__price-row"
      >
        <span>Minimum Order Value</span>
        <span>{{ formatCurrency(brandDetails.minimumOrderValue, brandDetails.currency) }}</span>
      </div>
    </div> -->

    <!-- Collection Schedule Link -->
    <div
      v-if="!isGuest && monthsWithCollection.length"
      class="v2-brand-preview__schedule-link"
      @click="showScheduleModal = true"
    >
      Collection Schedule
    </div>

    <!-- About -->
    <div v-if="brandDetails.storeDescription">
      <div class="v2-brand-preview__about-title">
        About
      </div>
      <div class="v2-brand-preview__about-text">
        <template v-if="isFullDescription">
          {{ brandDetails.storeDescription }}
        </template><template v-else>
          {{ truncatedDescription }}<template v-if="isDescriptionTruncated">
            …
          </template>
        </template><span
          v-if="isDescriptionTruncated"
          class="v2-brand-preview__read-more"
          @click="isFullDescription = !isFullDescription"
        >Read {{ isFullDescription ? 'Less' : 'More' }}</span>
      </div>
    </div>

    <!-- Products -->
    <div
      v-if="hasProductsSection"
      class="v2-brand-preview__enrich-section"
    >
      <div class="v2-brand-preview__enrich-title">
        Products
      </div>
      <div
        v-if="genderDisplay"
        class="v2-brand-preview__enrich-row"
      >
        <span class="v2-brand-preview__enrich-label">Gender:</span>
        <span class="v2-brand-preview__enrich-value">{{ genderDisplay }}</span>
      </div>
      <div v-if="productTypesList.length">
        <div class="v2-brand-preview__enrich-row">
          <span class="v2-brand-preview__enrich-label">Product types:</span>
          <span class="v2-brand-preview__enrich-value">
            {{ (isProductTypesExpanded ? productTypesList : productTypesList.slice(0, 2)).join(', ') }}
          </span>
        </div>
        <div
          v-if="productTypesList.length > 2"
          class="v2-brand-preview__enrich-toggle"
        >
          <span
            class="v2-brand-preview__read-more"
            @click="isProductTypesExpanded = !isProductTypesExpanded"
          >{{ isProductTypesExpanded ? 'Show less' : 'Show more' }}</span>
        </div>
      </div>
      <div v-if="categoriesList.length">
        <div class="v2-brand-preview__enrich-row">
          <span class="v2-brand-preview__enrich-label">Categories:</span>
          <span class="v2-brand-preview__enrich-value">
            {{ (isCategoriesExpanded ? categoriesList : categoriesList.slice(0, 2)).join(', ') }}
          </span>
        </div>
        <div
          v-if="categoriesList.length > 2"
          class="v2-brand-preview__enrich-toggle"
        >
          <span
            class="v2-brand-preview__read-more"
            @click="isCategoriesExpanded = !isCategoriesExpanded"
          >{{ isCategoriesExpanded ? 'Show less' : 'Show more' }}</span>
        </div>
      </div>
    </div>

    <!-- Brand Insights -->
    <div
      v-if="hasBrandInsights"
      class="v2-brand-preview__enrich-section"
    >
      <div class="v2-brand-preview__enrich-title">
        Brand Insights
      </div>
      <div
        v-if="priceRange"
        class="v2-brand-preview__enrich-row v2-brand-preview__enrich-row--inline"
      >
        <span class="v2-brand-preview__enrich-label">Range:</span>
        <span class="v2-brand-preview__enrich-value">
          {{ priceRange }}
          <span v-if="brandDetails.avgRetailPrice">{{ brandDetails.avgRetailPrice }}</span>
        </span>
      </div>
      <div
        v-if="brandDetails.customerPriceDescription"
        class="v2-brand-preview__enrich-text"
      >
        {{ brandDetails.customerPriceDescription }}
      </div>
      <div v-if="brandDetails.uniqueSellingPoints && brandDetails.uniqueSellingPoints.length">
        <div class="v2-brand-preview__enrich-sub-title">
          Brand unique facts
        </div>
        <div
          v-for="(usp, i) in (isInsightsExpanded ? brandDetails.uniqueSellingPoints : brandDetails.uniqueSellingPoints.slice(0, 1))"
          :key="i"
          class="v2-brand-preview__enrich-text"
        >
          {{ usp }}
        </div>
        <div
          v-if="brandDetails.uniqueSellingPoints.length > 1"
          class="v2-brand-preview__enrich-toggle"
        >
          <span
            class="v2-brand-preview__read-more"
            @click="isInsightsExpanded = !isInsightsExpanded"
          >{{ isInsightsExpanded ? 'Show less' : 'Show more' }}</span>
        </div>
      </div>
    </div>

    <!-- Commercial -->
    <div
      v-if="hasCommercial"
      class="v2-brand-preview__enrich-section"
    >
      <div class="v2-brand-preview__enrich-title">
        Commercial
      </div>
      <div
        v-if="brandDetails.channelsOnline && brandDetails.channelsOnline.length"
        class="v2-brand-preview__enrich-row v2-brand-preview__enrich-row--stacked"
      >
        <span class="v2-brand-preview__enrich-label">Channels of Sales:</span>
        <ul class="v2-brand-preview__enrich-list">
          <li
            v-for="(channel, i) in brandDetails.channelsOnline"
            :key="`channel-${i}`"
            class="v2-brand-preview__enrich-list-item"
          >
            {{ channel }}
          </li>
        </ul>
      </div>
      <div
        v-if="brandDetails.geographicPresence && brandDetails.geographicPresence.length"
        class="v2-brand-preview__enrich-row v2-brand-preview__enrich-row--stacked"
      >
        <span class="v2-brand-preview__enrich-label">Geographic Presence:</span>
        <ul class="v2-brand-preview__enrich-list">
          <li
            v-for="(region, i) in brandDetails.geographicPresence"
            :key="`region-${i}`"
            class="v2-brand-preview__enrich-list-item"
          >
            {{ region }}
          </li>
        </ul>
      </div>
      <template v-if="brandDetails.keyStockists && brandDetails.keyStockists.length">
        <div class="v2-brand-preview__enrich-sub-title">
          Key Stockists:
        </div>
        <div
          v-for="(stockist, i) in (isStockistsExpanded ? brandDetails.keyStockists : brandDetails.keyStockists.slice(0, 5))"
          :key="stockist.name || i"
          class="v2-brand-preview__enrich-stockist"
        >
          <a
            v-if="stockist.website"
            :href="getLinkWithhttp(stockist.website)"
            target="_blank"
            rel="noopener noreferrer"
            class="v2-brand-preview__enrich-stockist-name v2-brand-preview__enrich-stockist-name--link"
          >
            {{ stockist.name }}
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            ><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line
              x1="10"
              y1="14"
              x2="21"
              y2="3"
            /></svg>
          </a>
          <span
            v-else
            class="v2-brand-preview__enrich-stockist-name"
          >{{ stockist.name }}</span>
          <span
            v-if="stockist.country"
            class="v2-brand-preview__enrich-stockist-country"
          >{{ stockist.country }}</span>
        </div>
        <div
          v-if="brandDetails.keyStockists.length > 5"
          class="v2-brand-preview__enrich-toggle"
        >
          <span
            class="v2-brand-preview__read-more"
            @click="isStockistsExpanded = !isStockistsExpanded"
          >{{ isStockistsExpanded ? 'Show less' : 'Show more' }}</span>
        </div>
        <a
          v-if="brandDetails.allStockistsUrl"
          :href="getLinkWithhttp(brandDetails.allStockistsUrl)"
          target="_blank"
          rel="noopener noreferrer"
          class="v2-brand-preview__enrich-link v2-brand-preview__enrich-link--mt"
        >View all stockists</a>
      </template>
    </div>

    <!-- Modals -->
    <v2-schedule-meeting-modal
      :show="showMeetingModal"
      :brand-details="brandDetails"
      @close="showMeetingModal = false"
    />
    <v2-schedule-collection-modal
      :show="showScheduleModal"
      :brand-details="brandDetails"
      :months-with-collection="monthsWithCollection"
      :is-subscribed="isSubscribed"
      :scheduled-collection-types="scheduledCollectionTypes"
      @close="showScheduleModal = false"
      @handle-subscription="onHandleSubscription"
    />
  </div>
</template>

<script>
import V2Button from '@/components/v2/V2Button.vue'
import V2Badge from '@/components/v2/V2Badge.vue'
import { getLinkWithhttp } from '@/common-utils'
import { getAvatarColor } from '@/utils/avatar-utils'
import UserRoleMixin from '../../apps/UserRoleMixin.vue'
import V2ScheduleMeetingModal from './V2ScheduleMeetingModal.vue'
import V2ScheduleCollectionModal from './V2ScheduleCollectionModal.vue'

export default {
  name: 'V2BrandPreviewCard',
  components: {
    V2Button,
    V2Badge,
    V2ScheduleMeetingModal,
    V2ScheduleCollectionModal,
  },
  mixins: [UserRoleMixin],
  props: {
    brandDetails: { type: Object, default: () => ({}) },
    brandLogo: { type: String, default: '' },
    hasSocialLinks: { type: Boolean, default: false },
    monthsWithCollection: { type: Array, default: () => [] },
    isSubscribed: { type: Boolean, default: false },
    scheduledCollectionTypes: { type: Object, default: () => ({}) },
    formatCurrency: { type: Function, default: () => '' },
    textTruncate: { type: Function, default: v => v },
    onHandleSubscription: { type: Function, default: () => {} },
  },
  data() {
    return {
      isFullDescription: false,
      isProductTypesExpanded: false,
      isCategoriesExpanded: false,
      isInsightsExpanded: false,
      isStockistsExpanded: false,
      showMeetingModal: false,
      showScheduleModal: false,
      logoError: false,
      getLinkWithhttp,
    }
  },
  computed: {
    brandInitial() {
      const name = this.brandDetails.entityName || ''
      return name.charAt(0).toUpperCase()
    },
    avatarColor() {
      return getAvatarColor(this.brandDetails.entityName)
    },
    genderDisplay() {
      const g = this.brandDetails.genderFocus
      return Array.isArray(g) && g.length ? g.join(', ') : ''
    },
    productTypesList() {
      const cats = this.brandDetails.productCategories
      if (!Array.isArray(cats) || !cats.length) return []
      return cats.map(c => c.category).filter(Boolean)
    },
    categoriesList() {
      const cats = this.brandDetails.productCategories
      if (!Array.isArray(cats) || !cats.length) return []
      return cats.flatMap(c => c.subcategories || [])
    },
    priceRange() {
      const min = this.brandDetails.customerPriceMin
      const max = this.brandDetails.customerPriceMax
      const hasMin = min != null
      const hasMax = max != null
      if (!hasMin && !hasMax) return ''
      if (hasMin && hasMax) return `$${min} – $${max}`
      if (hasMin) return `From $${min}`
      return `Up to $${max}`
    },
    hasProductsSection() {
      return !!(this.genderDisplay || this.productTypesList.length || this.categoriesList.length)
    },
    hasBrandInsights() {
      const d = this.brandDetails
      return !!(
        d.customerPriceMin != null
        || d.customerPriceMax != null
        || d.customerPriceDescription
        || (Array.isArray(d.uniqueSellingPoints) && d.uniqueSellingPoints.length)
      )
    },
    hasCommercial() {
      const d = this.brandDetails
      return !!(
        (Array.isArray(d.channelsOnline) && d.channelsOnline.length)
        || (Array.isArray(d.geographicPresence) && d.geographicPresence.length)
        || (Array.isArray(d.keyStockists) && d.keyStockists.length)
      )
    },
    instagramHandle() {
      const link = this.brandDetails.instagramLink
      if (!link) return ''
      const match = link.match(/instagram\.com\/([^/?#]+)/i)
      if (match && match[1]) return `@${match[1]}`
      return link
    },
    isDescriptionTruncated() {
      const desc = this.brandDetails.storeDescription
      return typeof desc === 'string' && desc.length > 150
    },
    truncatedDescription() {
      const desc = this.brandDetails.storeDescription || ''
      if (desc.length <= 150) return desc
      const slice = desc.substr(0, 150)
      const lastSpace = slice.lastIndexOf(' ')
      return lastSpace > 0 ? slice.substr(0, lastSpace) : slice
    },
  },
  watch: {
    'brandDetails.logo': function () {
      this.logoError = false
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-brand-preview {
  width: 100%;
  padding: var(--v2-space-5);
  padding-top: 0;
  padding-left: 0;

  &__logo-wrap {
    display: flex;
    justify-content: flex-start;
  }

  &__logo {
    width: 80px;
    height: 80px;
    border-radius: 14px;
    object-fit: cover;
  }

  &__logo-fallback {
    width: 80px;
    height: 80px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: var(--v2-font-sans);
    font-size: 1.5rem;
    font-weight: 700;
    color: #ffffff;
  }

  &__name {
    font-family: var(--v2-font-sans);
    font-size: 1.25rem;
    font-weight: 700;
    text-align: left;
    color: var(--v2-foreground);
    margin-top: var(--v2-space-3);
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-1);
    margin-top: var(--v2-space-3);
  }

  &__info-row {
    display: flex;
    align-items: center;
    gap: var(--v2-space-2);
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
    text-decoration: none;

    &--link {
      color: var(--v2-primary);

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__info-icon {
    flex-shrink: 0;
  }

  &__departments {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: var(--v2-space-2);
    margin-top: var(--v2-space-6);
  }

  &__actions {
    display: flex;
    gap: var(--v2-space-2);
    margin-top: var(--v2-space-5);
  }

  &__prices {
    margin-top: var(--v2-space-5);
    font-family: var(--v2-font-sans);
    font-size: 1rem;
    color: var(--v2-foreground);
  }

  &__price-row {
    display: flex;
    justify-content: space-between;
    padding: var(--v2-space-1) 0;
  }

  &__schedule-link {
    margin-top: var(--v2-space-6);
    font-family: var(--v2-font-sans);
    font-size: 1rem;
    font-weight: 500;
    color: var(--v2-foreground);
    text-decoration: underline;
    cursor: pointer;
  }

  &__about-title {
    font-family: var(--v2-font-sans);
    font-size: 1rem;
    font-weight: 700;
    color: var(--v2-foreground);
    margin-top: var(--v2-space-6);
    margin-bottom: var(--v2-space-2);
    text-align: left;
  }

  &__about-text {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
    line-height: 1.5;
  }

  &__read-more {
    font-weight: 700;
    text-decoration: underline;
    cursor: pointer;
    color: var(--v2-foreground);
  }

  &__enrich-section {
    margin-top: var(--v2-space-6);
  }

  &__enrich-title {
    font-family: var(--v2-font-sans);
    font-size: 1rem;
    font-weight: 700;
    color: var(--v2-foreground);
    margin-bottom: var(--v2-space-3);
  }

  &__enrich-toggle {
    text-align: right;
    margin-top: var(--v2-space-1);
  }

  &__enrich-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    flex-wrap: wrap;
    padding: var(--v2-space-1) 0;
    gap: var(--v2-space-2);

    &--top {
      align-items: flex-start;
    }

    &--inline {
      justify-content: flex-start;

      .v2-brand-preview__enrich-label {
        min-width: 0;
      }

      .v2-brand-preview__enrich-value {
        flex: initial;
        text-align: left;
      }
    }

    &--stacked {
      flex-direction: column;
      align-items: stretch;
      gap: var(--v2-space-1);

      .v2-brand-preview__enrich-value {
        text-align: left;
      }
    }
  }

  &__enrich-label {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
    flex-shrink: 0;
    min-width: 5rem;
  }

  &__enrich-value {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
    font-weight: 500;
    text-align: right;
    flex: 1;
    min-width: 0;
  }

  &__enrich-sub-title {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--v2-foreground);
    margin-top: var(--v2-space-3);
    margin-bottom: var(--v2-space-1);
  }

  &__enrich-text {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
    line-height: 1.5;
  }

  &__enrich-link {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-primary);
    text-decoration: none;
    display: block;
    line-height: 1.6;

    &:hover {
      text-decoration: underline;
    }

    &--mt {
      margin-top: var(--v2-space-2);
    }
  }

  &__enrich-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  &__enrich-list-item {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
    font-weight: 500;
    line-height: 1.5;
  }

  &__enrich-stockist {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--v2-space-2);
    padding: var(--v2-space-2) 0;
    border-bottom: 1px solid var(--v2-border);

    &:last-of-type {
      border-bottom: none;
    }
  }

  &__enrich-stockist-name {
    display: inline-flex;
    align-items: center;
    gap: var(--v2-space-1);
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
    font-weight: 500;
    text-decoration: none;

    &--link {
      color: var(--v2-primary);

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__enrich-stockist-country {
    font-family: var(--v2-font-sans);
    font-size: 0.6875rem;
    color: var(--v2-muted-foreground);
    background: var(--v2-muted);
    padding: 2px var(--v2-space-2);
    border-radius: 4px;
    white-space: nowrap;
    flex-shrink: 0;
  }
}

</style>
